import { USE_EXTERNAL_API } from '@m/config/env';
import { ClientPlatform } from '@m/types/grpc/MemberOnboardingSource';
import { apiFetch, apiFetchV2 } from '@m/utils/http';
import { trackUserLoggedIn } from 'apps/customer/utils/heapOnboardTracking';
import { withValidationError } from 'apps/customer/utils/phoneVerificationErrors';

import type { User } from '@m/types/api';
import type {
  AcquisitionType,
  OnboardingFlow,
  ProfileFieldSource,
} from '@m/types/grpc/MemberOnboardingSource';

type VerifyAndRegisterUser = Pick<
  User,
  | 'active'
  | 'addedAt'
  | 'email'
  | 'firstName'
  | 'id'
  | 'isRegistered'
  | 'lastName'
  | 'phoneNumber'
  | 'updatedAt'
>;

export type VerifyAndRegisterApiResponse = {
  isNewAccount: boolean;
  token?: string;
  user: VerifyAndRegisterUser;
};

async function getUser() {
  const response = await apiFetch<{ user: User }>('/user');

  if (response.success) {
    trackUserLoggedIn(!!response.data?.user?.isRegistered);
  } else {
    trackUserLoggedIn(false);
  }

  return response;
}

const UserService = {
  getUser,

  logout: () => apiFetch('/logout', { method: 'POST' }),

  getUserSession: () => apiFetch('/user/visit/state'),

  parseInvitationCode: ({ invitationCode }: { invitationCode?: string }) =>
    apiFetch('/user/invitation', {
      params: {
        invitationCode,
      },
    }),

  parseMemberReferralInvitationCode: ({ invitationCode }: { invitationCode?: string }) =>
    apiFetch('/member/redeem-invitation', {
      params: {
        secret: invitationCode,
      },
    }),

  signUpWithInvitation: ({
    invitationCode,
    firstName,
    lastName,
    email,
    password,
  }: {
    email?: string;
    firstName?: string;
    invitationCode?: string;
    lastName?: string;
    password?: string;
  }) =>
    apiFetch('/user/invitation', {
      method: 'POST',
      params: { invitationCode },
      body: {
        firstName,
        lastName,
        email,
        password,
      },
    }),

  // No invitation code is required
  signUpRegister: ({
    firstName,
    lastName,
    email,
    password,
    phoneNumber,
    agreements,
    acquisitionType,
    onboardingFlow,
    profileFieldSource,
  }: {
    acquisitionType?: AcquisitionType;
    agreements?: { id: number; type: { id: number } }[];
    email?: string;
    firstName?: string;
    lastName?: string;
    onboardingFlow?: OnboardingFlow;
    password?: string;
    phoneNumber?: string;
    profileFieldSource?: ProfileFieldSource;
  }) =>
    apiFetch('/register', {
      method: 'POST',
      body: {
        provider: 'metropolis',
        firstName,
        lastName,
        email,
        password,
        phoneNumber,
        agreements,
        acquisitionType,
        onboardingFlow,
        profileFieldSource,
      },
    }),

  /**
   * Registers an unregistered customer
   *
   * This route accepts latest agreements and updates the user registration status
   */
  registerCustomerAndAcceptTerms: ({
    provider,
    acquisitionType,
    onboardingFlow,
    profileFieldSource,
    siteUuid,
  }: {
    acquisitionType?: AcquisitionType;
    onboardingFlow?: OnboardingFlow;
    profileFieldSource?: ProfileFieldSource;
    provider: 'metropolis'; // only metropolis for now
    siteUuid?: string;
  }) =>
    apiFetch('/v2/register', {
      method: 'POST',
      body: {
        provider,
        acquisitionType,
        onboardingFlow,
        profileFieldSource,
        siteUuid,
        clientPlatform: ClientPlatform.WEB,
      },
    }),

  // Sends verification code if user exists or doesn't exist
  requestCode: ({ hcaptchaToken, phoneNumber }: { hcaptchaToken?: string; phoneNumber: string }) =>
    apiFetch('/user/verification/send', {
      method: 'POST',
      body: {
        phoneNumber,
        ...(hcaptchaToken && { hcaptchaToken }),
      },
    }).then(withValidationError),

  verifyCode: ({
    phoneNumber,
    code: secret,
    beginSession, // Stores session in cookie, else receives session token
  }: {
    beginSession: boolean;
    code: string;
    phoneNumber?: string | null;
  }) => {
    const sessionMode = beginSession ? 'cookie' : undefined;
    return apiFetch('/user/verification', {
      params: {
        phoneNumber,
        secret,
        sessionMode: USE_EXTERNAL_API ? 'token' : sessionMode,
      },
    });
  },

  // Verifies the OTP code and auto-registers the user in one step (express onboarding).
  // POST /user/verification/register — added by EN-9585
  verifyAndRegister: ({
    phoneNumber,
    code: secret,
    beginSession,
    acquisitionType,
    onboardingFlow,
    profileFieldSource,
    siteUuid,
  }: {
    acquisitionType?: AcquisitionType;
    beginSession: boolean;
    code: string;
    onboardingFlow?: OnboardingFlow;
    phoneNumber?: string | null;
    profileFieldSource?: ProfileFieldSource;
    siteUuid?: string;
  }) => {
    const sessionMode = beginSession ? 'cookie' : undefined;
    return apiFetchV2<VerifyAndRegisterApiResponse>('/user/verification/register', {
      method: 'POST',
      params: {
        phoneNumber,
        secret,
        sessionMode: USE_EXTERNAL_API ? 'token' : sessionMode,
      },
      body: {
        acquisitionType,
        onboardingFlow,
        profileFieldSource,
        siteUuid,
        clientPlatform: ClientPlatform.WEB,
      },
    });
  },

  updateUserInfo: ({
    phoneNumber,
    email,
    firstName,
    lastName,
    profileFieldSource,
  }: {
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    phoneNumber?: string | null;
    profileFieldSource?: ProfileFieldSource;
  }) =>
    apiFetch('/customer/update-info', {
      method: 'PUT',
      body: {
        phoneNumber,
        email,
        firstName,
        lastName,
        profileFieldSource,
      },
    }),

  getActiveSubscriptions: () => apiFetch('/customer/subscription'),

  deleteAccount: (userId: number) =>
    apiFetch(`/customer/${userId}`, {
      method: 'DELETE',
    }),

  requestUpdateVerificationCode: ({ phoneNumber }: { phoneNumber: string }) =>
    apiFetch('/user/verification/update-phone/send', {
      method: 'POST',
      body: {
        phoneNumber,
      },
    }).then(withValidationError),

  verifyUpdateCode: ({ phoneNumber, secret }: { phoneNumber: string | null; secret: string }) =>
    apiFetch('/user/verify-phone-update', {
      method: 'POST',
      body: {
        phoneNumber,
        secret,
      },
    }),
};

export default UserService;
